import { Command } from 'cmdk'
import { Dialog as DialogPrimitive } from 'radix-ui'
import { CornerDownLeft, Search, type LucideIcon } from 'lucide-react'
import { useEffect } from 'react'
import { Kbd } from './primitives'

export interface CommandItem {
  id: string
  label: string
  icon: LucideIcon
  hint?: string
  keywords?: string[]
  disabled?: boolean
  onSelect: () => void
}

export interface CommandGroup {
  heading: string
  items: CommandItem[]
}

/** ⌘K / Ctrl+K toggles the palette from anywhere. */
export function useCommandHotkey(toggle: () => void): void {
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault()
        toggle()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [toggle])
}

export function CommandPalette({
  open,
  onOpenChange,
  groups,
  placeholder = 'Search or jump to…',
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  groups: CommandGroup[]
  placeholder?: string
}) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-slate-950/40 backdrop-blur-[2px] data-[state=open]:animate-in data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content className="fixed top-[12vh] left-1/2 z-50 w-[calc(100%-2rem)] max-w-xl -translate-x-1/2 overflow-hidden rounded-2xl border border-border bg-surface shadow-elevated data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95">
          <DialogPrimitive.Title className="sr-only">Command palette</DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">
            Search pages and actions, then press Enter.
          </DialogPrimitive.Description>
          <Command loop className="flex flex-col">
            <div className="flex items-center gap-3 border-b border-border px-4">
              <Search className="size-4 text-muted-foreground" />
              <Command.Input
                autoFocus
                placeholder={placeholder}
                className="h-12 flex-1 bg-transparent text-[15px] outline-none placeholder:text-muted-foreground"
              />
              <Kbd>Esc</Kbd>
            </div>
            <Command.List className="max-h-[min(60vh,420px)] scrollbar-thin overflow-y-auto p-2">
              <Command.Empty className="py-10 text-center text-sm text-muted-foreground">
                No results found.
              </Command.Empty>
              {groups.map((group) => (
                <Command.Group
                  key={group.heading}
                  heading={group.heading}
                  className="[&_[cmdk-group-heading]]:px-2.5 [&_[cmdk-group-heading]]:pt-2 [&_[cmdk-group-heading]]:pb-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground"
                >
                  {group.items.map((item) => (
                    <Command.Item
                      key={item.id}
                      value={`${group.heading} ${item.label}`}
                      keywords={item.keywords ?? []}
                      disabled={item.disabled ?? false}
                      onSelect={() => {
                        onOpenChange(false)
                        item.onSelect()
                      }}
                      className="group flex cursor-pointer items-center gap-3 rounded-lg px-2.5 py-2 text-sm outline-none select-none data-[disabled=true]:cursor-not-allowed data-[disabled=true]:opacity-45 data-[selected=true]:bg-muted"
                    >
                      <span className="grid size-7 place-items-center rounded-md border border-border bg-surface-2 text-muted-foreground group-data-[selected=true]:border-brand-500/30 group-data-[selected=true]:text-primary">
                        <item.icon className="size-3.5" />
                      </span>
                      <span className="flex-1 truncate">{item.label}</span>
                      {item.hint && <span className="text-xs text-muted-foreground">{item.hint}</span>}
                      <CornerDownLeft className="size-3.5 text-muted-foreground opacity-0 group-data-[selected=true]:opacity-100" />
                    </Command.Item>
                  ))}
                </Command.Group>
              ))}
            </Command.List>
          </Command>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
